import React from "react";
import { AlertTriangle, ArrowRight, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CompanyCandidatesList } from "@/shared/ui";

interface AddBrandStepFindProps {
  fetchUrl: string;
  onFetchUrlChange: (value: string) => void;
  onFetchBrandDetails: () => void;
  isPending: boolean;
  isDetailsFetched: boolean;
  searchCandidates: Array<{ name: string; domain: string; logoUrl?: string | null }> | null;
  onSelectCandidate: (domain: string) => void;
  existingBrandMatch: { brandId: string; brandName: string } | null;
  onUseExistingBrand: () => void;
  hasOnSuccess: boolean;
}

export const AddBrandStepFind: React.FC<AddBrandStepFindProps> = ({
  fetchUrl,
  onFetchUrlChange,
  onFetchBrandDetails,
  isPending,
  isDetailsFetched,
  searchCandidates,
  onSelectCandidate,
  existingBrandMatch,
  onUseExistingBrand,
  hasOnSuccess,
}) => {
  return (
    <div className="relative flex items-start gap-4">
      {/* Timeline Connector Line (1 -> 2) */}
      <div className="absolute left-4 top-9 bottom-[-24px] w-[2px] bg-slate-100 overflow-hidden">
        <div
          className={`w-full bg-[#A655F8] transition-all duration-500 ease-out ${
            isDetailsFetched ? "h-full" : "h-0"
          }`}
        />
      </div>

      {/* Step Circle */}
      <div className="w-8 h-8 rounded-full bg-[#A655F8] text-white flex items-center justify-center text-sm font-semibold shrink-0 shadow-sm z-10">
        1
      </div>

      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-slate-900">Find Brand</h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Search by brand name, or paste their website URL.
        </p>

        <div className="mt-3 flex flex-col sm:flex-row gap-2.5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={fetchUrl}
              onChange={(e) => onFetchUrlChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onFetchBrandDetails();
                }
              }}
              placeholder="e.g. Adidas or adidas.co.in"
              className="pl-10 h-11 rounded-xl border-slate-200 text-sm placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[#A655F8] focus:border-[#A655F8] focus:outline-none transition-colors"
            />
          </div>

          <Button
            type="button"
            onClick={onFetchBrandDetails}
            disabled={!fetchUrl.trim() || isPending}
            className="h-11 px-5 rounded-xl bg-[#A655F8] hover:bg-[#9333EA] text-white font-medium text-sm flex items-center justify-center gap-2 shrink-0 transition-colors shadow-none"
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Fetching...</span>
              </>
            ) : (
              <>
                <span>Find Brand</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">
          We&apos;ll automatically fetch the brand name, logo and website for you.
        </p>

        {/* Name search results */}
        <CompanyCandidatesList
          candidates={searchCandidates}
          onSelectCandidate={onSelectCandidate}
          isLoading={isPending}
          headerText="Select the correct brand"
          emptyTitle="No matching brands found"
          emptySubtitle="Try a different spelling, or paste the brand's website URL instead."
          className="mt-3"
        />

        {/* Duplicate detected banner */}
        {existingBrandMatch && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/80 p-3.5 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex items-start gap-2.5">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-amber-900">
                  {existingBrandMatch.brandName} is already in your portfolio
                </p>
                <p className="text-[11px] text-amber-700 mt-0.5">
                  A brand with this domain already exists in this organization.
                </p>
                {hasOnSuccess && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="mt-2 text-xs h-7 bg-white border-amber-300 text-amber-900 hover:bg-amber-100"
                    onClick={onUseExistingBrand}
                  >
                    Use existing brand
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
